import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Input from './Input';
import Select from './Select';
import AsyncSelect from './AsyncSelect';
import TextArea from './TextArea';
import Button from './Button';
import { Product } from '../../types';
import { inventoryCategoriesService } from '../../services';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { useCurrencies } from '../../hooks/useCurrencies';

interface ProductFormProps {
  onSubmit: (data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => void;
  initialData?: Product;
  onCancel: () => void;
}

// Server-side search + pagination for the category dropdown
const fetchCategories = async (query: string, page: number) => {
  return inventoryCategoriesService.getAll({
    filters: query ? { name: { $containsi: query } } : undefined,
    pagination: { page, pageSize: 20 },
    sort: ['name:asc']
  }) as any;
};

// Currency may come back populated as an object; the form needs the documentId
const normalizeProduct = (product: Product): Product => ({
  ...product,
  currency: (typeof product.currency === 'object' && product.currency !== null
    ? ((product.currency as any).documentId || String((product.currency as any).id))
    : product.currency) as any,
  lastRestockDate: formatDateForInput(product.lastRestockDate)
});

const ProductForm: React.FC<ProductFormProps> = ({ onSubmit, initialData, onCancel }) => {
  // Fetch currencies from API
  const { data: currencies } = useCurrencies();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset
  } = useForm<Product>({
    defaultValues: initialData ? normalizeProduct(initialData) : {
      code: '',
      name: '',
      description: '',
      categoryId: '',
      quantity: 0,
      unit: 'pcs',
      unitPrice: 0,
      currency: '',
      minStockLevel: 10,
      maxStockLevel: 100,
      supplier: '',
      location: '',
      isActive: true,
      notes: ''
    }
  });

  useEffect(() => {
    if (initialData) {
      reset(normalizeProduct(initialData));
    }
  }, [initialData, reset]);

  // Re-apply the saved currency once the async options are loaded (edit case)
  useEffect(() => {
    if (initialData && currencies && currencies.length > 0) {
      const normalized = normalizeProduct(initialData);
      if (normalized.currency) setValue('currency', normalized.currency);
    }
  }, [initialData, currencies, setValue]);

  const handleFormSubmit = (data: Product) => {
    const submitData = {
      ...data,
      lastRestockDate: formatDateForAPI(data.lastRestockDate)
    };
    onSubmit(submitData);
    if (!initialData) {
      reset();
    }
  };

  const unitOptions = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'liter', label: 'Liters' },
    { value: 'meter', label: 'Meters' },
    { value: 'box', label: 'Boxes' },
    { value: 'carton', label: 'Cartons' },
    { value: 'pack', label: 'Packs' },
    { value: 'unit', label: 'Units' }
  ];

  // Currency options from API - NO HARDCODED VALUES
  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) {
      return [];
    }
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.code || ''} (${currency.name || currency.code || 'Unknown'})`
      }));
  }, [currencies]);

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Product Code"
          type="text"
          placeholder="e.g., PROD-001"
          required
          {...register('code', { required: 'Product code is required' })}
          error={errors.code?.message}
        />

        <Input
          label="Product Name"
          type="text"
          placeholder="e.g., Engine Oil"
          required
          {...register('name', { required: 'Product name is required' })}
          error={errors.name?.message}
        />
      </div>

      <TextArea
        label="Description"
        {...register('description')}
        error={errors.description?.message}
        placeholder="Product description..."
        rows={3}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Controller
          name="categoryId"
          control={control}
          rules={{ required: 'Category is required' }}
          render={({ field }) => (
            <AsyncSelect
              label="Category"
              name="categoryId"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={errors.categoryId?.message}
              required
              placeholder="Select category..."
              fetchOptions={fetchCategories}
              getOptionLabel={(cat) => cat.name || ''}
              getOptionValue={(cat) => cat.documentId || String(cat.id)}
            />
          )}
        />

        <Input
          label="Supplier"
          type="text"
          placeholder="Supplier name"
          required
          {...register('supplier', { required: 'Supplier is required' })}
          error={errors.supplier?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Input
          label="Quantity"
          type="number"
          step="1"
          placeholder="0"
          required
          {...register('quantity', { 
            required: 'Quantity is required',
            valueAsNumber: true,
            min: { value: 0, message: 'Quantity cannot be negative' }
          })}
          error={errors.quantity?.message}
        />

        <Select
          label="Unit"
          {...register('unit')}
          options={unitOptions}
          error={errors.unit?.message}
        />

        <Input
          label="Location"
          type="text"
          placeholder="e.g., Warehouse A"
          required
          {...register('location', { required: 'Location is required' })}
          error={errors.location?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Unit Price"
          type="number"
          step="0.01"
          placeholder="0.00"
          required
          {...register('unitPrice', { 
            required: 'Unit price is required',
            valueAsNumber: true,
            min: { value: 0, message: 'Price cannot be negative' }
          })}
          error={errors.unitPrice?.message}
        />

        <Select
          label="Currency"
          {...register('currency')}
          options={currencyOptions}
          placeholder="Select currency"
          error={errors.currency?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Minimum Stock Level"
          type="number"
          step="1"
          placeholder="10"
          required
          {...register('minStockLevel', { 
            required: 'Minimum stock level is required',
            valueAsNumber: true,
            min: { value: 0, message: 'Cannot be negative' }
          })}
          error={errors.minStockLevel?.message}
        />

        <Input
          label="Maximum Stock Level"
          type="number"
          step="1"
          placeholder="100"
          required
          {...register('maxStockLevel', { 
            required: 'Maximum stock level is required',
            valueAsNumber: true,
            min: { value: 1, message: 'Must be at least 1' }
          })}
          error={errors.maxStockLevel?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Last Restock Date"
          type="date"
          {...register('lastRestockDate')}
          error={errors.lastRestockDate?.message}
        />

        <Select
          label="Status"
          {...register('isActive', { 
            setValueAs: (value) => value === 'true'
          })}
          options={statusOptions}
          error={errors.isActive?.message}
        />
      </div>

      <TextArea
        label="Notes"
        {...register('notes')}
        error={errors.notes?.message}
        placeholder="Additional notes..."
        rows={3}
      />

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
        >
          {initialData ? 'Update Product' : 'Add Product'}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;

